import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Map } from 'immutable';
import { useDebounce } from 'use-debounce';
import queryString from 'query-string';

// MUI components
import { withStyles } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import Icon from '@material-ui/core/Icon';
import TextField from '@material-ui/core/TextField';

// local components
import Button from '../../Button';
import ChaptersTable from './components/ChaptersTable';
import HeaderCard from './components/HeaderCard';
import AddChapterModal from './components/AddChapterModal';

const propTypes = {
  classes:                PropTypes.instanceOf(Object).isRequired,
  fetchChapters:          PropTypes.func.isRequired,
  currentUser:            PropTypes.instanceOf(Map).isRequired,
  chapter:                PropTypes.instanceOf(Map).isRequired,
  fetchAnalytics:         PropTypes.func.isRequired,
  analytics:              PropTypes.instanceOf(Map).isRequired,
  clearStoreForDashboard: PropTypes.func.isRequired,
};

function Home({
  classes,
  fetchChapters,
  currentUser,
  chapter,
  fetchAnalytics,
  analytics,
  clearStoreForDashboard,
}) {
  // Get search value from URL first
  const urlParams = queryString.parse(window.location.search);
  const urlSearch = urlParams.search;
  const chapterData = chapter.get('data') || Map();

  const [displayAddChapterModal, setDisplayAddChapterModal] = useState(false);
  const [search, setSearch] = useState(urlSearch || '');
  const [sortBy, setSortBy] = useState('name');
  const [skip, setSkip] = useState(chapterData.get('skip') || 0);
  const [limit, setLimit] = useState(chapterData.get('limit') || 10);
  const [order, setOrder] = useState(1);

  const [debouncedSearch] = useDebounce(search, 500);

  const clearanceLevel = (currentUser.get('data') || Map()).get('clearance_level') || 0;

  const fetchParams = useMemo(() => {
    const params = {
      sort: {},
      limit,
      skip,
    };

    if (debouncedSearch) {
      params.search = debouncedSearch;
    }

    params.sort[sortBy] = order;

    return params;
  }, [debouncedSearch, sortBy, order, limit, skip]);

  const handleSearchChange = ({ target: { value } }) => {
    setSearch(value);
  };

  const handleSortChange = (property) => {
    let newOrder = order;

    if (property === sortBy) {
      newOrder *= -1;
    } else {
      newOrder = 1;
    }

    setSortBy(property);
    setOrder(newOrder);
  };

  const handlePaginationChange = (params) => {
    if (params.limit !== limit) setLimit(params.limit);
    if (params.skip !== skip) setSkip(params.skip);
  };

  const updateURL = (searchValue) => {
    const url = new URL(window.location);

    if (searchValue) {
      url.searchParams.set('search', searchValue);
    } else {
      url.searchParams.delete('search');
    }

    // Update URL without triggering page reload
    window.history.replaceState({}, '', url);
  };

  const handleCloseAddChapterModal = () => setDisplayAddChapterModal(false);
  const handleOpenAddChapterModal = () => setDisplayAddChapterModal(true);

  // Render search loading indicator
  const renderSearchLoadingIndicator = () => {
    let element;

    if (chapter.get('loading')) {
      element = <CircularProgress size={ 20 } />;
    }

    return element;
  };

  // Initial mount (componentWillMount equivalent)
  useEffect(() => {
    clearStoreForDashboard();
  }, []);

  useEffect(() => {
    if (clearanceLevel > 0) fetchAnalytics();
  }, [clearanceLevel, fetchAnalytics]);

  useEffect(() => {
    setSkip(0);
    if (debouncedSearch) {
      updateURL(debouncedSearch);
    } else {
      updateURL(null);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    if (clearanceLevel > 0) fetchChapters(fetchParams);
  }, [clearanceLevel, fetchParams, fetchChapters]);

  return (
    <Grid container alignItems='center' spacing={ 16 }>
      {
        clearanceLevel > 0 ? (
          <Grid item xs={ 8 } align='left' key='chapterSearch'>
            <div className={ classes.searchFieldContainer }>
              <Icon className={ classes.searchFieldIcon } color='primary'>search</Icon>
              <TextField InputProps={ { disableUnderline: true } }
                placeholder='Search for chapters'
                value={ search || '' }
                onChange={ handleSearchChange }
                fullWidth />

              { renderSearchLoadingIndicator() }
            </div>
          </Grid>
        ) : null
      }

      {
        clearanceLevel === 2 ? [
          <Hidden xsDown key='addChapterSmall'>
            <Grid item sm={ 4 } className={ classes.topButton } align='right'>
              <Button variant='contained' color='primary' onClick={ handleOpenAddChapterModal }>
                <Icon className={ classes.leftIcon }>add</Icon>
                Add Chapter
              </Button>
            </Grid>
          </Hidden>,

          <Hidden smUp key='addChapterLarge'>
            <Grid item sm={ 4 } className={ classes.topButton } align='right'>
              <Button variant='fab' color='primary' onClick={ handleOpenAddChapterModal }>
                <Icon>add</Icon>
              </Button>
            </Grid>
          </Hidden>,

          <Grid item xs={ 12 } key='headerCard'>
            <HeaderCard chapter={ chapter }
              analytics={ analytics } />
          </Grid>,
        ] : null
      }

      <Grid item xs={ 12 } className={ classes.tableContainer }>
        <ChaptersTable onPaginationChange={ handlePaginationChange }
          onSortChange={ handleSortChange }
          search={ search }
          sortBy={ sortBy }
          order={ order } />
      </Grid>

      <AddChapterModal open={ displayAddChapterModal }
        onClose={ handleCloseAddChapterModal } />
    </Grid>
  );
}

Home.propTypes = propTypes;

const styles = theme => ({
  searchFieldContainer: {
    alignItems:      'center',
    display:         'flex',
    backgroundColor: theme.palette.common.white,
    borderRadius:    30,
    boxShadow:       '0 6px 20px 0 rgba(157, 157, 157, .7)',
    maxWidth:        300,
    padding:         '5px 20px 5px 40px',
    position:        'relative',
    marginLeft:      theme.spacing.unit,
  },

  searchFieldIcon: {
    left:     9,
    position: 'absolute',
    top:      9,
  },

  leftIcon: {
    marginRight: theme.spacing.unit * 2,
  },

  tableContainer: {
    [theme.breakpoints.down('xs')]: {
      width: 'auto',
    },
    [theme.breakpoints.up('sm')]: {
      width: '100%',
    },
  },

  topButton: {
    [theme.breakpoints.down('xs')]: {
      marginLeft: 40,
    },
  },
});

export default withStyles(styles)(Home);
